/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { CommandBarButton, IButton } from '@fluentui/react/lib/Button';
import { ICommandBarItemProps } from '@fluentui/react/lib/CommandBar';
import { IContextualMenuItem } from '@fluentui/react/lib/ContextualMenu';
import { IRefObject, getId } from '@fluentui/react/lib/Utilities';
import { observer } from 'mobx-react';
import React from 'react';
import { defineMessages } from 'react-intl';
import { LuisCommandBar } from 'src/core/components/LuisCommandBar';
import { RBACTooltip } from 'src/core/components/RBACTooltip';
import { TableCommandBarProps } from 'src/core/components/TableCommandBarProps';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { FileType } from 'src/core/types/FileType';
import { getCommandBarButtonStyles } from 'src/core/utils/azureRbacStyles';
import { mapUserRoleToUserActions } from 'src/core/utils/azureUtils';
import { createTeachingBubble, dequeueTeachingBubble } from 'src/luis/projects/events/utils/teachingBubbles';
import { QnaMakerSynonymStore } from 'src/qnaMaker/core/stores/QnaMakerSynonymStore';
import { qnaMakerTeachingBubbleMessages } from 'src/qnaMaker/core/utils/qnaMakerTeachingBubbleMessages';

export type QnaMakerSynonymGroupCommandBarProps = TableCommandBarProps<QnaMakerSynonymStore> & {
    /**
     * A boolean flag indicating if there are unsaved changes.
     */
    isInDirtyState: boolean;

    /**
     * A boolean flag indicating if there are no synonyms.
     */
    isInEmptyState: boolean;

    /**
     * A callback on delete button click.
     */
    onDelete: () => void;

    /**
     * A callback on import button click.
     */
    onImport: (fileFormat: Extract<FileType, 'xlsx' | 'tsv'>) => void;

    /**
     * A callback on export button click.
     */
    onExport: (fileFormat: Extract<FileType, 'xlsx' | 'tsv'>) => void;

    /**
     * A boolean flag indicating if there are no synonyms.
     */
    isAddSynonymOn: boolean;

    /**
     * A callback on add synonym button click.
     */
    onAddNewSynonym: () => void;

    /**
     * A boolean flag indicating if there is a save operation in the background
     * to disable all edit actions.
     */
    areEditActionsDisabled: boolean;

    /**
     * An Object containing the settings for the import project modal.
     */
    importProjectModalSettings: {
        hidden: boolean;
        fileType: 'tsv' | 'xlsx';
    };
};

const messages = defineMessages({
    addSynonyms: {
        id: 'qnaMaker.qnaMakerSynonymGroupCommandBar.addSynonyms',
        defaultMessage: 'Add'
    },
    saveChanges: {
        id: 'qnaMaker.qnaMakerSynonymGroupCommandBar.saveChanges',
        defaultMessage: 'Save changes'
    },
    edit: {
        id: 'qnaMaker.qnaMakerSynonymGroupCommandBar.edit',
        defaultMessage: 'Edit'
    },
    deleteSynonymGroup: {
        id: 'qnaMaker.qnaMakerSynonymGroupCommandBar.deleteSynonymGroup',
        defaultMessage: 'Delete'
    },
    deleteFollowUpPrompts: {
        id: 'qnaMaker.qnaMakerSynonymGroupCommandBar.deleteFollowUpPrompts',
        defaultMessage: 'Delete follow up prompts'
    },
    importExport: {
        id: 'qnaMaker.qnaMakerSynonymGroupCommandBar.importExport',
        defaultMessage: 'Import/Export'
    },
    import: {
        id: 'qnaMaker.qnaMakerSynonymGroupCommandBar.import',
        defaultMessage: 'Import'
    },
    export: {
        id: 'qnaMaker.qnaMakerSynonymGroupCommandBar.export',
        defaultMessage: 'Export'
    },
    exportSynonymsExcel: {
        id: 'qnaMaker.qnaMakerSynonymGroupCommandBar.exportSynonymsExcel',
        defaultMessage: 'Export as Excel'
    },
    exportSynonymsTsv: {
        id: 'qnaMaker.qnaMakerSynonymGroupCommandBar.exportSynonymsTsv',
        defaultMessage: 'Export as TSV'
    },
    importSynonymsExcel: {
        id: 'qnaMaker.qnaMakerSynonymGroupCommandBar.importSynonymsExcel',
        defaultMessage: 'Import as Excel'
    },
    importSynonymsTsv: {
        id: 'qnaMaker.qnaMakerSynonymGroupCommandBar.importSynonymsTsv',
        defaultMessage: 'Import as TSV'
    }
});

/**
 * Command bar rendering all available models and filter options
 */
export const QnaMakerSynonymGroupCommandBar = observer((props: QnaMakerSynonymGroupCommandBarProps) => {
    const {
        onDelete,
        onExport,
        onImport,
        onAddNewSynonym,
        isInDirtyState,
        isAddSynonymOn,
        isInEmptyState,
        areEditActionsDisabled,
        importProjectModalSettings
    } = props;

    const { localize } = useLocalization();
    const { uiStore, rootStore } = useGlobalState();

    const addButtonIdRef = React.useRef<string>(getId());
    const importExportButtonIdRef: IRefObject<IButton> = React.useRef<IButton>(null);
    const importButtonIdRef = React.useRef<string>(getId());
    const exportButtonIdRef = React.useRef<string>(getId());
    const deleteButtonIdRef = React.useRef<string>(getId());

    const [isAddButtonDisabled, setIsAddButtonDisabled] = React.useState(true);
    const [isDeleteButtonDisabled, setIsDeleteButtonDisabled] = React.useState(true);

    const { userRole, displayedUserRole } = rootStore.serviceKey;
    const { canWrite } = mapUserRoleToUserActions(userRole);

    React.useEffect(
        () => () => {
            dequeueTeachingBubble('SynonymEdit');
            dequeueTeachingBubble('SynonymDelete');
            dequeueTeachingBubble('QnaAddSynonyms');
            dequeueTeachingBubble('SynonymSaveChanges');
        },
        []
    );

    React.useEffect(() => {
        if (!isAddButtonDisabled && !uiStore.teachingBubbles.QnaAddSynonyms) {
            createTeachingBubble(
                'QnaAddSynonyms',
                localize(qnaMakerTeachingBubbleMessages.addSynonymBody),
                localize(qnaMakerTeachingBubbleMessages.addSynonymTitle),
                `#${addButtonIdRef.current}`,
                true,
                true
            );

            return;
        }

        if (
            !isDeleteButtonDisabled &&
            uiStore.teachingBubbles.SynonymEdit &&
            !uiStore.teachingBubbles.QnaDelete &&
            !uiStore.teachingBubbles.SynonymDelete &&
            !uiStore.skippedTeachingBubbles.SynonymEdit
        ) {
            createTeachingBubble(
                'SynonymDelete',
                localize(qnaMakerTeachingBubbleMessages.synonymDeleteBody),
                localize(qnaMakerTeachingBubbleMessages.synonymDeleteTitle),
                `#${deleteButtonIdRef.current}`,
                true
            );
        }
    }, [isInDirtyState, isAddButtonDisabled, isDeleteButtonDisabled, Object.values(uiStore.teachingBubbles)]);

    // this use effect is to set the focus on the import/export button when the modal is closed
    React.useEffect(() => {
        let timeoutId: NodeJS.Timeout | null = null;

        if (importProjectModalSettings.hidden) {
            timeoutId = setTimeout(() => {
                importExportButtonIdRef.current?.focus();
            }, 0);
        }

        return () => {
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
        };
    }, [importProjectModalSettings.hidden]);

    const importExportMenuItems: IContextualMenuItem[] = [
        {
            key: 'import',
            id: importButtonIdRef.current,
            text: localize(messages.import),
            iconProps: { iconName: 'Upload' },
            ariaLabel: localize(messages.import),
            disabled: !canWrite,
            role: 'menuitem',
            subMenuProps: {
                ariaLabel: localize(messages.import),
                items: [
                    { key: 'importExcel', role: 'menuitem', name: localize(messages.importSynonymsExcel), onClick: () => onImport('xlsx') },
                    { key: 'importTsv', role: 'menuitem', name: localize(messages.importSynonymsTsv), onClick: () => onImport('tsv') }
                ]
            },
            componentRef(ref) {
                if (!ref) {
                    importExportButtonIdRef.current?.focus();
                }
            }
        },
        {
            key: 'export',
            id: exportButtonIdRef.current,
            text: localize(messages.export),
            iconProps: { iconName: 'Download' },
            ariaLabel: localize(messages.export),
            disabled: isInEmptyState,
            role: 'menuitem',
            subMenuProps: {
                ariaLabel: localize(messages.export),
                items: [
                    { key: 'exportExcel', role: 'menuitem', name: localize(messages.exportSynonymsExcel), onClick: () => onExport('xlsx') },
                    { key: 'exportTsv', role: 'menuitem', name: localize(messages.exportSynonymsTsv), onClick: () => onExport('tsv') }
                ]
            }
        }
    ];

    const items: ICommandBarItemProps[] = [
        {
            key: 'add',
            onRender: (item: ICommandBarItemProps) => {
                const _disabled = item.disabled || isAddSynonymOn || areEditActionsDisabled;
                setIsAddButtonDisabled(_disabled);

                return (
                    <RBACTooltip
                        hidden={canWrite || _disabled}
                        displayedUserRole={displayedUserRole}
                        data-automation-id="addSynonymsButtonLanguageToolTip"
                        componentSurroundedByToolTip={
                            <CommandBarButton
                                role="menuitem"
                                onClick={onAddNewSynonym}
                                id={addButtonIdRef.current}
                                iconProps={{ iconName: 'Add' }}
                                disabled={!canWrite || _disabled}
                                name={localize(messages.addSynonyms)}
                                text={localize(messages.addSynonyms)}
                                data-automation-id="addSynonymsButton"
                                ariaLabel={localize(messages.addSynonyms)}
                                styles={getCommandBarButtonStyles(item.renderedInOverflow)}
                            />
                        }
                    />
                );
            }
        },
        {
            key: 'importAndExport',
            onRender: (item: ICommandBarItemProps) => (
                <RBACTooltip
                    displayedUserRole={displayedUserRole}
                    hidden={canWrite || item.disabled || areEditActionsDisabled}
                    data-automation-id="importSynonymsButtonLanguageToolTip"
                    componentSurroundedByToolTip={
                        <CommandBarButton
                            role={'menuitem'}
                            componentRef={importExportButtonIdRef}
                            name={localize(messages.importExport)}
                            text={localize(messages.importExport)}
                            iconProps={{ iconName: 'Snow' }}
                            ariaLabel={localize(messages.importExport)}
                            data-automation-id="importExportSynonymsButton"
                            menuProps={{
                                id: 'importExportMenuItems',
                                items: importExportMenuItems,
                                ariaLabel: localize(messages.importExport)
                            }}
                            styles={getCommandBarButtonStyles(item.renderedInOverflow)}
                            disabled={!canWrite || item.disabled || areEditActionsDisabled}
                        />
                    }
                />
            )
        },
        {
            key: 'delete',
            onRender: (item: ICommandBarItemProps) => {
                const _disabled = item.disabled || areEditActionsDisabled || isInEmptyState;
                setIsDeleteButtonDisabled(_disabled);

                return (
                    <RBACTooltip
                        hidden={canWrite || _disabled}
                        displayedUserRole={displayedUserRole}
                        data-automation-id="deleteSynonymsButtonLanguageToolTip"
                        componentSurroundedByToolTip={
                            <CommandBarButton
                                role="menuitem"
                                onClick={onDelete}
                                id={deleteButtonIdRef.current}
                                disabled={!canWrite || _disabled}
                                iconProps={{ iconName: 'Delete' }}
                                data-automation-id="deleteSynonymsButton"
                                name={localize(messages.deleteSynonymGroup)}
                                text={localize(messages.deleteSynonymGroup)}
                                ariaLabel={localize(messages.deleteSynonymGroup)}
                                styles={getCommandBarButtonStyles(item.renderedInOverflow)}
                            />
                        }
                    />
                );
            }
        }
    ];

    return <LuisCommandBar items={items} onReduceData={() => undefined} />;
});
